import { Logo } from "@/components/branding/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { getClientPortalBase, setClientTenant } from "@/lib/clientPortalPaths";
import { clientAuthService } from "@/services/clientAuth.service/clientAuth.api";
import { AlertCircle, Lock, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

export default function ClientAccountClaim() {
  const { token, tenant } = useParams<{ token: string; tenant?: string }>();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [needsEmail, setNeedsEmail] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // SaaS multi-tenant — a URL sempre carrega a slug da organização (/:tenant/c/claim/:token);
  // fixa o tenant atual antes de navegar pro portal, senão o redirect final pode cair na
  // organização errada se o navegador já tiver outro tenant salvo de uma sessão anterior.
  useEffect(() => {
    if (tenant) setClientTenant(tenant);
  }, [tenant]);

  useEffect(() => {
    if (!token) {
      setError("Link inválido ou expirado.");
      return;
    }
    void clientAuthService.getClaimInfo(token).then(([info, err]) => {
      if (err || !info) {
        setError(err?.msg || "Este link é inválido ou já expirou. Peça pra gente enviar um novo link.");
        return;
      }
      setNeedsEmail(info.needsEmail);
    });
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setError("Link inválido ou expirado.");
      return;
    }
    if (needsEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast({
        title: "Erro",
        description: "Digite um e-mail válido",
        variant: "destructive",
      });
      return;
    }
    if (!password || password.length < 6) {
      toast({
        title: "Erro",
        description: "A senha deve ter pelo menos 6 caracteres",
        variant: "destructive",
      });
      return;
    }
    if (password !== confirmPassword) {
      toast({
        title: "Erro",
        description: "As senhas não conferem",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    const [res, err] = await clientAuthService.claimAccount(token, password, needsEmail ? email.trim() : undefined);
    setIsLoading(false);

    if (err || !res?.accessToken) {
      setError(
        err?.msg ||
          "Este link é inválido ou já expirou. Peça pra gente enviar um novo link."
      );
      return;
    }

    toast({
      title: "Bem-vindo!",
      description: "Sua conta foi confirmada. Redirecionando…",
    });
    navigate(`${getClientPortalBase()}/tickets`, { replace: true });
  };

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <h1 className="text-xl font-semibold">Link inválido</h1>
        <p className="max-w-sm text-muted-foreground text-sm">{error}</p>
        <Button asChild variant="outline">
          <Link to={`${getClientPortalBase()}/login`}>Voltar ao login</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="bg-card shadow-lg rounded-lg p-8 w-full max-w-md animate-fadeIn">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Logo size={32} textClassName="text-2xl" />
          </div>
          <h1 className="text-2xl font-bold">Confirme sua conta</h1>
          <p className="mt-2 text-muted-foreground text-sm">
            Encontramos seu cadastro pelo telefone. Defina uma senha para acessar o portal.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {needsEmail && (
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium">
                E-mail
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="voce@exemplo.com"
                  className="pl-10"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm font-medium">
              Senha
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                className="pl-10"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="confirmPassword" className="block text-sm font-medium">
              Confirmar senha
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Repita a senha"
                className="pl-10"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? "Confirmando..." : "Confirmar e entrar"}
          </Button>
        </form>
      </div>
    </div>
  );
}
